// SC-2654 / SC-2767 / SC-3532 / SC-3533 — the Findings view's filter + sort model. Pure +
// exported so the URL round-trip, the server params, and the header-sort cycle are
// unit-testable without a store, a router, or react-query.
//
// The categorical axes (status / scope / kind / field) are MULTI-SELECT (arrays; an EMPTY
// array is unconstrained), matching the entities facet rail. `minSeverity` is a
// single-select FLOOR; `sort` + `sortDir` is one column order, set from the table headers.
//
// SC-3532 — EVERY axis is a SERVER param, and the list is paged on the server. Filtering or
// sorting the loaded page in the browser is wrong past the first page (the old 100-row
// ceiling left 954 of Hoffmaster's 1,054 findings unreachable and every count short), so
// nothing here filters or sorts findings itself. Scope moved server-side once the stored
// `Finding.scope` was verified current (no stale rows on staging or production).
import type { FindingsSortColumn, FindingsSortDir, ListFindingsParams } from '../../../api/endpoints';
import type { FacetChoice } from '../facets/CategoricalFacet';
import type {
  CanvasDirective,
  FindingFieldCount,
  FindingStatus,
  FindingsSort as FindingsDirectiveSort,
  FindingsSortDirection,
  Scope,
} from '../../../types';

// A finding is a "fix" (carries a suggested change) or "review" (flagged for a human).
export type Kind = 'fix' | 'review';
// A minimum-severity FLOOR (single-select): keep findings at or above this band.
export type MinSeverity = 'low' | 'medium' | 'high' | 'critical' | 'all';
// The sortable table columns (SC-3532) — Finding (the summary) is not sortable.
export type SortKey = FindingsSortColumn;
export type SortDir = FindingsSortDir;

export interface FindingsFilters {
  // Multi-select — an EMPTY array is unconstrained (all).
  status: FindingStatus[];
  scope: Scope[];
  kind: Kind[];
  // SC-3533 — exact field names; a finding touching ANY of them is kept.
  field: string[];
  // Single-select: a min-severity FLOOR.
  minSeverity: MinSeverity;
  // The column order, from the table headers.
  sort: SortKey;
  sortDir: SortDir;
}

// The direction a column starts in when its header is first clicked — the order a person
// expects to read it in: most severe / most affected first, fields A→Z.
export const NATURAL_DIR: Record<SortKey, SortDir> = {
  severity: 'desc',
  affected: 'desc',
  field: 'asc',
};

// Findings open as the *pending* list, most severe first — the single surface to act.
// Every other axis defaults to unconstrained.
export const DEFAULT_FILTERS: FindingsFilters = {
  status: ['pending'],
  scope: [],
  kind: [],
  field: [],
  minSeverity: 'all',
  sort: 'severity',
  sortDir: 'desc',
};

// Allowed members per axis — the single source of truth for both the facet options
// and URL validation (an unknown member is dropped so a hand-edited URL can't wedge
// the view). No `'all'` sentinel on the multi-select axes: empty selection = all.
// `field` has no fixed vocabulary — any name is kept; one the catalog doesn't have just
// matches nothing, and the rail still shows it so it can be cleared.
const STATUS_VALUES: FindingStatus[] = ['pending', 'acknowledged', 'applied', 'dismissed'];
const SCOPE_VALUES: Scope[] = ['value', 'field', 'catalog'];
const KIND_VALUES: Kind[] = ['fix', 'review'];
const SEVERITY_VALUES: MinSeverity[] = ['all', 'low', 'medium', 'high', 'critical'];
// A retired `?sort=queue` (pre-SC-3532 links, stored agent directives) is not listed, so it
// falls back to the default order.
const SORT_VALUES: SortKey[] = ['severity', 'affected', 'field'];
const SORT_DIR_VALUES: SortDir[] = ['asc', 'desc'];

// Page sizes the pager offers; the BE caps `per_page` at 100.
export const PER_PAGE_OPTIONS = [25, 50, 100] as const;
export type PerPage = (typeof PER_PAGE_OPTIONS)[number];
export const DEFAULT_PER_PAGE: PerPage = 50;

/** The filter axes as server params, without paging — also the key for the field
 *  filter's options, which take every filter EXCEPT the field axis itself (so its counts
 *  answer "how many if I add this field"). An EMPTY status list omits the param so the
 *  server returns every status. */
export function toFilterParams(
  filters: FindingsFilters,
  { includeField = true }: { includeField?: boolean } = {},
): ListFindingsParams {
  const params: ListFindingsParams = {};
  if (filters.status.length) params.status = filters.status;
  if (filters.scope.length) params.scope = filters.scope;
  if (filters.kind.length) params.kind = filters.kind;
  if (includeField && filters.field.length) params.field = filters.field;
  if (filters.minSeverity !== 'all') params.minSeverity = filters.minSeverity;
  return params;
}

/** One page of the list: every filter + the column sort + paging, all server-side. */
export function toListParams(filters: FindingsFilters, page: number, perPage: PerPage): ListFindingsParams {
  return { ...toFilterParams(filters), sort: filters.sort, dir: filters.sortDir, page, perPage };
}

/** A header click on column `key`: the active column flips direction; another column
 *  starts in its natural direction. There is no "unsorted" state — the list always has an
 *  order, and the default (severity, most severe first) is one of the columns. */
export function nextSort(filters: FindingsFilters, key: SortKey): Pick<FindingsFilters, 'sort' | 'sortDir'> {
  if (filters.sort === key) return { sort: key, sortDir: filters.sortDir === 'asc' ? 'desc' : 'asc' };
  return { sort: key, sortDir: NATURAL_DIR[key] };
}

// --- URL round-trip ---------------------------------------------------------

const scalarPick = <T extends string>(raw: string | null, allowed: T[], fallback: T): T =>
  allowed.includes(raw as T) ? (raw as T) : fallback;

/** Parse a multi-select param, tolerant of BOTH encodings — comma-joined
 *  (`?scope=value,field`, what we write) AND repeated (`?scope=value&scope=field`, a
 *  hand-crafted/shared link) — dropping any member not in `allowed` and de-duping. */
const listPick = <T extends string>(sp: URLSearchParams, key: string, allowed: readonly T[]): T[] => {
  const raw = sp.getAll(key).flatMap((v) => v.split(','));
  return [...new Set(raw.filter((v): v is T => (allowed as readonly string[]).includes(v)))];
};

/** Value sets equal regardless of order (both hold distinct members). */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

/** Status is special: its default is the non-empty pending queue. An ABSENT param
 *  means that default; a present-but-EMPTY param (`?status=`) is the explicit "all
 *  statuses"; a present param whose members are ALL invalid (a stale/hand-edited link
 *  like `?status=bogus`) falls back to the pending default rather than silently
 *  broadening to every status. */
function parseStatus(sp: URLSearchParams): FindingStatus[] {
  if (!sp.has('status')) return [...DEFAULT_FILTERS.status];
  const raw = sp.getAll('status').flatMap((v) => v.split(','));
  const valid = [
    ...new Set(raw.filter((v): v is FindingStatus => (STATUS_VALUES as readonly string[]).includes(v))),
  ];
  if (valid.length) return valid;
  // Nothing valid: a truly-empty param (`status=`) is the explicit "all"; a non-empty
  // but unrecognized one is malformed → the safe pending default.
  return raw.some((v) => v !== '') ? [...DEFAULT_FILTERS.status] : [];
}

/** Field names, one per repeated `?field=` param — NOT comma-joined like the other axes,
 *  because a CSV header can itself contain a comma. Empty values dropped, de-duped. */
function parseFields(sp: URLSearchParams): string[] {
  return [...new Set(sp.getAll('field').filter((v) => v !== ''))];
}

/** Read the filter state out of the URL; the inverse of {@link writeFilters}. Tolerant
 *  of stale/hand-edited links (invalid members dropped, repeated params merged). A
 *  missing or invalid `dir` is the column's natural direction. */
export function parseFilters(sp: URLSearchParams): FindingsFilters {
  const sort = scalarPick(sp.get('sort'), SORT_VALUES, DEFAULT_FILTERS.sort);
  return {
    status: parseStatus(sp),
    scope: listPick(sp, 'scope', SCOPE_VALUES),
    kind: listPick(sp, 'kind', KIND_VALUES),
    field: parseFields(sp),
    minSeverity: scalarPick(sp.get('sev'), SEVERITY_VALUES, DEFAULT_FILTERS.minSeverity),
    sort,
    sortDir: scalarPick(sp.get('dir'), SORT_DIR_VALUES, NATURAL_DIR[sort]),
  };
}

/** The current page (1-based) from `?page=`; anything unparseable is page 1. */
export function parsePage(sp: URLSearchParams): number {
  return Math.max(1, Number.parseInt(sp.get('page') ?? '1', 10) || 1);
}

/** The page size from `?per=`; only the offered sizes are honored. */
export function parsePerPage(sp: URLSearchParams): PerPage {
  const n = Number.parseInt(sp.get('per') ?? '', 10);
  return (PER_PAGE_OPTIONS as readonly number[]).includes(n) ? (n as PerPage) : DEFAULT_PER_PAGE;
}

function setList(sp: URLSearchParams, key: string, list: readonly string[]): void {
  if (list.length) sp.set(key, list.join(','));
  else sp.delete(key);
}
function setScalar(sp: URLSearchParams, key: string, value: string, def: string): void {
  if (value === def) sp.delete(key);
  else sp.set(key, value);
}

/** Write the filter state onto a copy of the given params, preserving unrelated keys
 *  (e.g. the overlay's `?finding=`). A default axis is deleted, keeping the URL clean.
 *  `status`'s default is non-empty (`['pending']`), so an explicit empty set (all
 *  statuses) writes `status=` (present-empty) — distinct from an absent, default one. */
export function writeFilters(prev: URLSearchParams, filters: FindingsFilters): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (sameSet(filters.status, DEFAULT_FILTERS.status)) next.delete('status');
  else next.set('status', filters.status.join(','));
  setList(next, 'scope', filters.scope);
  setList(next, 'kind', filters.kind);
  next.delete('field');
  filters.field.forEach((f) => next.append('field', f));
  setScalar(next, 'sev', filters.minSeverity, DEFAULT_FILTERS.minSeverity);
  setScalar(next, 'sort', filters.sort, DEFAULT_FILTERS.sort);
  // Direction is written only when it differs from the column's natural one.
  setScalar(next, 'dir', filters.sortDir, NATURAL_DIR[filters.sort]);
  return next;
}

/** True when every axis is at its default — drives clean URLs (no params) and the
 *  "genuinely empty" vs "filtered to empty" empty-state split. */
export function isDefaultFilters(filters: FindingsFilters): boolean {
  return (
    sameSet(filters.status, DEFAULT_FILTERS.status) &&
    filters.scope.length === 0 &&
    filters.kind.length === 0 &&
    filters.field.length === 0 &&
    filters.minSeverity === 'all' &&
    filters.sort === DEFAULT_FILTERS.sort &&
    filters.sortDir === DEFAULT_FILTERS.sortDir
  );
}

/** Number of active filter selections — the single source for BOTH the rail's header
 *  pill and the toolbar Filters badge, so they can't disagree. Status's default is the
 *  non-empty pending queue, so this reads 1 by default (pending IS an active filter).
 *  Sort is an ordering, not a filter, so it's excluded. */
export function findingsFilterCount(filters: FindingsFilters): number {
  return (
    filters.status.length +
    filters.scope.length +
    filters.kind.length +
    filters.field.length +
    (filters.minSeverity !== 'all' ? 1 : 0)
  );
}

function directiveSort(
  sort: FindingsDirectiveSort | null | undefined,
  dir: FindingsSortDirection | null | undefined,
): Pick<FindingsFilters, 'sort' | 'sortDir'> {
  const column: SortKey = sort && sort !== 'queue' ? sort : DEFAULT_FILTERS.sort;
  return { sort: column, sortDir: dir ?? NATURAL_DIR[column] };
}

/** Translate an agent `open_view` findings directive (SC-2766) into the Findings-view URL query
 *  string — the inverse of {@link parseFilters}, so the agent's filter intent lands as a
 *  shareable, refresh-surviving URL (no lossy store hop). Reuses {@link writeFilters}, so the
 *  same default-omission rules apply. A `null`/absent axis is the BE's "unconstrained":
 *   • status — SPECIAL: its FE default is the pending queue, so absent ⇒ pending, NOT all,
 *     matching the agent-facing tool description ("omit ⇒ the default pending queue"); the agent
 *     gets "all statuses" only by listing them. A bare findings nav (`params` null) is likewise
 *     the pending queue (empty query).
 *   • scope / kind — absent ⇒ [] (all).
 *   • minSeverity — absent ⇒ 'all' (no floor); the wire never carries 'all'.
 *   • field — absent ⇒ [] (every field).
 *   • sort — absent, or the retired 'queue' ⇒ the default severity column; `sortDir` absent ⇒
 *     that column's natural direction.
 *  A `finding` overlay rides along as `?finding=<id>` (the param the view reads for its detail
 *  overlay); other overlay kinds don't address the Findings view and are ignored here. */
export function findingsDirectiveToSearch(directive: CanvasDirective): string {
  let sp = new URLSearchParams();
  const p = directive.params;
  if (p && p.view === 'findings') {
    sp = writeFilters(sp, {
      // `null`/absent status ⇒ the pending default (the agent didn't constrain it); an explicit
      // empty list ⇒ "all statuses" (present-empty `status=`, the FE convention), NOT pending —
      // so `??` (nullish), not a length check, mirrors how scope/kind treat `[]`.
      status: p.status ?? [...DEFAULT_FILTERS.status],
      scope: p.scope ?? [],
      kind: p.kind ?? [],
      field: p.field ?? [],
      minSeverity: p.minSeverity ?? 'all',
      ...directiveSort(p.sort, p.sortDir),
    });
  }
  if (directive.overlay?.kind === 'finding') sp.set('finding', directive.overlay.id);
  return sp.toString();
}

/** The header count (SC-3532): the true total across every page, and — when a filter
 *  narrows the list — how many the selected statuses hold without it, so a filter can't
 *  quietly hide most of the findings. */
export function findingsCountLabel(total: number, unfilteredTotal: number): string | undefined {
  if (total === 0) return undefined;
  const noun = (n: number) => (n === 1 ? 'finding' : 'findings');
  if (unfilteredTotal > total) {
    return `${total.toLocaleString('en-US')} of ${unfilteredTotal.toLocaleString('en-US')} ${noun(unfilteredTotal)}`;
  }
  return `${total.toLocaleString('en-US')} ${noun(total)}`;
}

/** The Field facet's rows: every SELECTED field first, whatever the search text, with its
 *  count (0 when the options no longer list it — the other filters now exclude it, or a
 *  hand-edited link named it) — so an active filter always stays visible and can be
 *  unchecked; then the server's other fields, filtered by the search text, in its order. */
export function fieldFacetChoices(
  options: readonly FindingFieldCount[],
  selected: readonly string[],
  search: string,
): FacetChoice[] {
  // Picked fields come first and ignore the search: an active filter must never vanish
  // from the rail because the search text doesn't match it (Greptile P2 on #196).
  const counts = new Map(options.map((o) => [o.field, o.count]));
  const picked = selected.map((f) => ({ value: f, count: counts.get(f) ?? 0 }));
  const pickedSet = new Set(selected);
  const needle = search.trim().toLowerCase();
  const matching = options
    .filter((o) => !pickedSet.has(o.field) && (!needle || o.field.toLowerCase().includes(needle)))
    .map((o) => ({ value: o.field, count: o.count }));
  return [...picked, ...matching];
}

// --- Facet options (SC-2767) ------------------------------------------------
// value + label per multi-select axis for the facet rail — no `'all'` option (an
// empty selection IS "all"). Colocated with the axis definitions so the control and
// the validation can't drift. Severity renders as a band (see FindingsFacetRail);
// sort is set from the table headers (SC-3532), so it has no options list here.
export const STATUS_OPTIONS: { value: FindingStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'applied', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
];
export const SCOPE_OPTIONS: { value: Scope; label: string }[] = [
  { value: 'value', label: 'Value' },
  { value: 'field', label: 'Field' },
  { value: 'catalog', label: 'Catalog' },
];
export const KIND_OPTIONS: { value: Kind; label: string }[] = [
  { value: 'fix', label: '✦ Fix' },
  { value: 'review', label: '⚑ Review' },
];
